/**
 * Location Service - Handles geolocation and address resolution
 * Supports both mobile and desktop with graceful fallbacks
 */

class LocationService {
  constructor() {
    this.timeout = 10000; // 10 seconds timeout
    this.enableHighAccuracy = true;
  }

  /**
   * Check if geolocation is supported by the browser
   */
  isGeolocationSupported() {
    return 'geolocation' in navigator;
  }

  /**
   * Get current position using browser's geolocation API
   */
  async getCurrentPosition() {
    return new Promise((resolve, reject) => {
      if (!this.isGeolocationSupported()) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      const options = {
        enableHighAccuracy: this.enableHighAccuracy,
        timeout: this.timeout,
        maximumAge: 300000 // 5 minutes cache
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          let errorMessage = 'Unable to get your location';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location services and try again.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please enter your address manually.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again or enter your address manually.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting your location.';
              break;
          }
          
          reject(new Error(errorMessage));
        },
        options
      );
    });
  }

  /**
   * Reverse geocode coordinates to address using browser's built-in geocoding
   * Falls back to a simple coordinate-based lookup if needed
   */
  async reverseGeocode(latitude, longitude) {
    try {
      // Try using a free geocoding service first
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );
      
      if (!response.ok) {
        throw new Error('Geocoding service unavailable');
      }
      
      const data = await response.json();
      console.log('Geocoding API response:', data); // Debug log
      
      // Extract pincode from multiple possible fields
      let pincode = data.postcode || data.postalCode || data.zipCode || data.zip || '';
      
      // If no pincode found, try alternative geocoding service
      if (!pincode) {
        try {
          const altResponse = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
          );
          if (altResponse.ok) {
            const altData = await altResponse.json();
            console.log('Alternative geocoding response:', altData);
            pincode = altData.address?.postcode || '';
          }
        } catch (altError) {
          console.warn('Alternative geocoding failed:', altError);
        }
      }
      
      // Extract address components
      const address = {
        addressLine1: this.buildAddressLine1(data),
        addressLine2: this.buildAddressLine2(data),
        city: data.city || data.locality || data.localityInfo?.administrative?.[3]?.name || '',
        state: data.principalSubdivision || data.principalSubdivisionCode || '',
        pincode: pincode,
        country: data.countryName || 'India'
      };

      // Clean up empty fields
      Object.keys(address).forEach(key => {
        if (!address[key] || address[key].trim() === '') {
          address[key] = '';
        }
      });

      console.log('Parsed address:', address); // Debug log
      return address;
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      
      // Return a basic structure with coordinates info
      return {
        addressLine1: '',
        addressLine2: `Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
        city: '',
        state: '',
        pincode: '',
        country: 'India',
        note: 'Please verify and complete your address details'
      };
    }
  }

  /**
   * Build address line 1 from geocoding data
   */
  buildAddressLine1(data) {
    const parts = [];
    
    if (data.streetNumber) parts.push(data.streetNumber);
    if (data.street) parts.push(data.street);
    
    return parts.join(' ') || '';
  }

  /**
   * Build address line 2 from geocoding data
   */
  buildAddressLine2(data) {
    const parts = [];
    
    if (data.neighbourhood) parts.push(data.neighbourhood);
    if (data.suburb) parts.push(data.suburb);
    
    return parts.join(', ') || '';
  }

  /**
   * Get current location and resolve to address
   * Main method to be used by components
   */
  async getCurrentLocationAddress() {
    try {
      // Step 1: Get coordinates
      const position = await this.getCurrentPosition();
      
      // Step 2: Convert to address
      const address = await this.reverseGeocode(position.latitude, position.longitude);
      
      return {
        success: true,
        address,
        coordinates: position,
        message: 'Location detected successfully! Please verify the address details.'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        address: null,
        coordinates: null
      };
    }
  }

  /**
   * Request location permission (for better UX)
   */
  async requestLocationPermission() {
    if (!this.isGeolocationSupported()) {
      return { granted: false, reason: 'Geolocation not supported' };
    }

    try {
      // Try to get permission status if available
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        return {
          granted: permission.state === 'granted',
          reason: permission.state
        };
      }
      
      // Fallback: just return that we need to try
      return { granted: null, reason: 'unknown' };
    } catch (error) {
      return { granted: null, reason: 'unknown' };
    }
  }
}

// Export singleton instance
const locationService = new LocationService();
export default locationService;